import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './components/Login/Login';
import Register from './components/Register/Register';
import Unsubscribe from './components/Unsubscribe/Unsubscribe';
import Confirm from './components/Register/Confirm';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import ResetPassword from './components/ForgotPassword/ResetPassword';
import TopBar from './components/TopBar/TopBar';
import Navbar from './components/Navbar/Navbar';
import Dashboard from './components/Dashboard/Dashboard';
import Academy from './components/Academy/Academy';
import SuiExplainer from './components/Academy/SuiExplainer';
import Challenges from './components/Academy/Challenges';
import TheoryOverview from './components/Academy/Theory/TheoryOverview';
import TheoryStage from './components/Academy/Theory/TheoryStage';
import Settings from './components/Settings/Settings';
import Challenge from './components/Challenge/Challenge';
import Leaderboard from './components/Leaderboard/Leaderboard';
import Missions from './components/Missions/Missions';
import Profile from './components/Profile/Profile';
import TechQuiz from './components/TechQuiz/TechQuiz';
import QuizResult from './components/TechQuiz/QuizResult';
import { TECH_QUIZZES } from './components/TechQuiz/registry';

import AdminChallenges from './components/Admin/AdminChallenges';
import AdminAddChallenge from './components/Admin/AdminAddChallenge';
import AdminUpdateChallenge from "./components/Admin/AdminUpdateChallenge";
import AdminUsers from "./components/Admin/AdminUsers";
import AdminEmails from "./components/Admin/AdminEmails";
import AdminTechQuizzes from "./components/Admin/AdminTechQuizzes";
import AdminTheoryStages from './components/Admin/AdminTheoryStages';
import AdminAddTheoryStage from './components/Admin/AdminAddTheoryStage';
import AdminUpdateTheoryStage from './components/Admin/AdminUpdateTheoryStage';
import AdminTheoryQuestions from './components/Admin/AdminTheoryQuestions';
import AdminAddTheoryQuestion from './components/Admin/AdminAddTheoryQuestion';
import AdminUpdateTheoryQuestion from './components/Admin/AdminUpdateTheoryQuestion';
import AdminIntel from './components/Admin/Intel/AdminIntel';
import IntelUser from './components/Admin/Intel/IntelUser';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import { Link as RouterLink } from 'react-router-dom';

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import TermsAndConditions from './components/TermsAndConditions/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import ScrollToTop from "./utils/ScrollTop";

import { getUserData } from "./api/Api";
import PageBackground from "./shared/PageBackground";
import { useColors } from "./theme/ColorModeContext";
import BrandLockup from "./shared/BrandLockup";
import { RESEARCHZERO_URL } from "./shared/brand";

import ReactGA from "react-ga4";


const TRACKING_ID = "G-FES4RX8HPV";
ReactGA.initialize(TRACKING_ID);

const PublicLayout = ({ children }: { children: React.ReactNode }) => {
  const colors = useColors();

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: colors.bg.overlay,
          backdropFilter: 'blur(24px) saturate(1.8)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
          borderBottom: `1px solid ${colors.border.subtle}`,
          boxShadow: '0 12px 36px rgba(0,0,0,0.04)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px !important' }}>
          <BrandLockup to="/" size="medium" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              component="a"
              href={RESEARCHZERO_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              ResearchZero
            </Button>
            <Button component={RouterLink} to="/login" variant="outlined" size="small">
              Login
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      {children}
    </>
  );
};

function useToken() {
  function getToken() {
    const tokenString = localStorage.getItem("token");
    if(tokenString) {
      // A corrupt value here used to throw out of this useState initialiser,
      // which blanks the whole app with no way back except clearing site data.
      // Anything unparseable is treated as "logged out" and dropped instead.
      try {
        return JSON.parse(tokenString);
      } catch {
        localStorage.removeItem("token");
        return "";
      }
    }
    return "";
  }
  
  const [token, setToken] = useState(getToken());
  const saveToken = useCallback((userToken: any) => {
    localStorage.setItem("token", JSON.stringify(userToken));
    setToken(userToken);
  }, []);
  
  return {
    token,
    setToken: saveToken
  }
}

function App() {
  const { token, setToken } = useToken();
  const [username, setUsername] = useState("");
  const [admin, setAdmin] = useState(false);
  const [smallMenu, setSmallMenu] = useState(false);


  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });

    let cancelled = false;

    const updateUser = async () => {
        const data = await getUserData(token);
        if(cancelled) return; // token changed (e.g. logout) while this request was in flight
        if(data) {
            setUsername(data.username);
            setAdmin(data.admin);
            if(data.access_token !== "" && token !== data.access_token) {
              setToken(data.access_token);
            }
        } else {
            setToken("");
        }
    }

    const interval = setInterval(updateUser, 20 * 60 * 1000); // 20 minutes

    if(token) updateUser();

    return () => {
        cancelled = true;
        clearInterval(interval);
    };
  }, [token, setToken]);

  if(!token) {
      return (
        <BrowserRouter>
          <PageBackground />
          <ScrollToTop />
          <Routes>
            <Route path="/confirm/:token" element={<Confirm />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            {/* Reachable signed out on purpose: an unsubscribe link that
                redirects to /login is not a working unsubscribe link. */}
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/login" element={<Login setToken={setToken} />} />
            <Route path="/leaderboard" element={<PublicLayout><Leaderboard token="" /></PublicLayout>} />
            <Route path="/profile/:username" element={<PublicLayout><Profile /></PublicLayout>} />
            {TECH_QUIZZES.map((qz) => (
              <Route
                key={qz.slug}
                path={qz.path}
                element={<PublicLayout><TechQuiz slug={qz.slug} title={qz.title} topic={qz.topic} questions={qz.questions} /></PublicLayout>}
              />
            ))}
            {/* Opened by whoever a result was shared with, so it must work signed out.
                The API's share page (handlers/tech_quiz.py) redirects here; keep the path in sync. */}
            <Route path="/quiz-result/:shareId" element={<PublicLayout><QuizResult /></PublicLayout>} />
            <Route path="/academy/ethereum-basics" element={<PublicLayout><TheoryOverview token="" category="ethereum-basics" /></PublicLayout>} />
            <Route path="/academy/ethereum-basics/theory/:stageSlug" element={<PublicLayout><TheoryStage token="" category="ethereum-basics" /></PublicLayout>} />
            <Route path="/academy/bitcoin-basics" element={<PublicLayout><TheoryOverview token="" category="bitcoin-basics" /></PublicLayout>} />
            <Route path="/academy/bitcoin-basics/theory/:stageSlug" element={<PublicLayout><TheoryStage token="" category="bitcoin-basics" /></PublicLayout>} />
            <Route path="/academy/solana" element={<PublicLayout><TheoryOverview token="" category="solana" /></PublicLayout>} />
            <Route path="/academy/solana/theory/:stageSlug" element={<PublicLayout><TheoryStage token="" category="solana" /></PublicLayout>} />
            <Route path="/academy/canton" element={<PublicLayout><TheoryOverview token="" category="canton" /></PublicLayout>} />
            <Route path="/academy/canton/theory/:stageSlug" element={<PublicLayout><TheoryStage token="" category="canton" /></PublicLayout>} />
            <Route path="/academy/starknet-basics" element={<PublicLayout><TheoryOverview token="" category="starknet-basics" /></PublicLayout>} />
            <Route path="/academy/starknet-basics/theory/:stageSlug" element={<PublicLayout><TheoryStage token="" category="starknet-basics" /></PublicLayout>} />
            <Route path="*" element={<Navigate to="/login" />}/>
          </Routes>
        </BrowserRouter>
      )
  }

  return (
    <BrowserRouter>
      <PageBackground />
      <TopBar token={token} username={username} setToken={setToken} admin={admin} />
      <Box sx={{ display: 'flex' }}>
        <Navbar
          admin={admin}
          smallMenu={smallMenu}
          setSmallMenu={setSmallMenu}
          token={token}
          username={username}
        />
        <Box sx={{
          flexGrow: 1,
          marginLeft: smallMenu ? '60px' : '220px',
          transition: 'margin-left 0.3s ease',
        }}>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={<Dashboard token={token} />} />
            <Route path="/academy" element={<Navigate to="/academy/ethereum-evm" />} />
            <Route path="/academy/ethereum-evm" element={<Academy token={token} track="ethereum-evm" />} />
            <Route path="/academy/canton-daml" element={<Academy token={token} track="canton-daml" />} />
            <Route path="/academy/solana-and-anchor" element={<Academy token={token} track="solana-and-anchor" />} />
            <Route path="/academy/bitcoin" element={<Academy token={token} track="bitcoin" />} />
            <Route path="/academy/bitcoin-basics" element={<TheoryOverview token={token} category="bitcoin-basics" />} />
            <Route path="/academy/bitcoin-basics/theory/:stageSlug" element={<TheoryStage token={token} category="bitcoin-basics" />} />
            <Route path="/academy/starknet-cairo" element={<Academy token={token} track="starknet-cairo" />} />
            <Route path="/academy/starknet-basics" element={<TheoryOverview token={token} category="starknet-basics" />} />
            <Route path="/academy/starknet-basics/theory/:stageSlug" element={<TheoryStage token={token} category="starknet-basics" />} />
            <Route path="/academy/sui-move" element={<Academy token={token} track="sui-move" />} />
            <Route path="/academy/sui-basics" element={<SuiExplainer />} />
            <Route path="/academy/ethereum-basics" element={<TheoryOverview token={token} category="ethereum-basics" />} />
            <Route path="/academy/ethereum-basics/theory/:stageSlug" element={<TheoryStage token={token} category="ethereum-basics" />} />
            <Route path="/academy/canton" element={<TheoryOverview token={token} category="canton" />} />
            <Route path="/academy/canton/theory/:stageSlug" element={<TheoryStage token={token} category="canton" />} />
            <Route path="/academy/solana" element={<TheoryOverview token={token} category="solana" />} />
            <Route path="/academy/solana/theory/:stageSlug" element={<TheoryStage token={token} category="solana" />} />
            <Route path="/academy/:category" element={<Challenges token={token} />} />
            <Route path="/academy/challenge/:id" element={<Challenge token={token}/>} />

            <Route path="/missions" element={<Missions token={token} /> } />
            <Route path="/missions/:id" element={<Challenge token={token}/>} />

            <Route path='/leaderboard' element={<Leaderboard token={token} username={username} />} />
            <Route path='/profile/:username' element={<Profile />} />
            <Route path='/settings' element={<Settings token={token} setToken={setToken} />} />
            {TECH_QUIZZES.map((qz) => (
              <Route
                key={qz.slug}
                path={qz.path}
                element={<TechQuiz slug={qz.slug} title={qz.title} topic={qz.topic} questions={qz.questions} token={token} />}
              />
            ))}
            <Route path="/quiz-result/:shareId" element={<QuizResult />} />

            { admin && <Route path='/admin/challenges' element={<AdminChallenges token={token} />} /> }
            { admin && <Route path='/admin/challenges/add' element={<AdminAddChallenge token={token} />} /> }
            { admin && <Route path='/admin/challenge/:id' element={<AdminUpdateChallenge token={token} />} /> }
            { admin && <Route path='/admin/users' element={<AdminUsers token={token} />} /> }
            { admin && <Route path='/admin/intel' element={<AdminIntel token={token} />} /> }
            { admin && <Route path='/admin/intel/user/:id' element={<IntelUser token={token} />} /> }
            { admin && <Route path='/admin/emails' element={<AdminEmails token={token} />} /> }
            { admin && <Route path='/admin/tech-quizzes' element={<AdminTechQuizzes token={token} />} /> }

            { admin && <Route path='/admin/theory/stages' element={<AdminTheoryStages token={token} />} /> }
            { admin && <Route path='/admin/theory/stages/add' element={<AdminAddTheoryStage token={token} />} /> }
            { admin && <Route path='/admin/theory/stage/:id' element={<AdminUpdateTheoryStage token={token} />} /> }
            { admin && <Route path='/admin/theory/stage/:stage_id/questions' element={<AdminTheoryQuestions token={token} />} /> }
            { admin && <Route path='/admin/theory/stage/:stage_id/questions/add' element={<AdminAddTheoryQuestion token={token} />} /> }
            { admin && <Route path='/admin/theory/question/:id' element={<AdminUpdateTheoryQuestion token={token} />} /> }

            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="*" element={<Navigate to="/dashboard" />}/>
          </Routes>
        </Box>
      </Box>
    </BrowserRouter>
  );
}

export default App;
